import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import PublicHomepage from "./pages/PublicHomepage";
import LandingPage from "./pages/LandingPage";
import PublicCourseDetail from "./pages/PublicCourseDetail";
import Pricing from "./pages/Pricing";
import Features from "./pages/Features";
import XAgentsPage from "./pages/XAgentsPage";
import Resources from "./pages/Resources";
import About from "./pages/About";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import BlogIndexPage from "./pages/blog/BlogIndexPage";
import BlogPostPage from "./pages/blog/BlogPostPage";
import ProjectDashboard from "./pages/ProjectDashboard";
import Settings from "./pages/Settings";
import DashboardLayout from "./components/layouts/DashboardLayout";
import { PlaybooksLayout } from "./components/layouts/PlaybooksLayout";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ResetPassword from "./pages/auth/ResetPassword";
import RedditCallback from "./pages/auth/RedditCallback";
import ContentTopicsPage from "./pages/content/ContentTopicsPage";
import SocialTopicsPage from "./pages/content/SocialTopicsPage";
import GeneratedContentPage from "./pages/content/GeneratedContentPage";
import ProjectsPage from "./pages/Projects";
import AnalyzePage from "./pages/AnalyzePage";
import ResearchPage from "./pages/ResearchPage";
import ContentEditorPage from "@/pages/content/ContentEditorPage";
import PlaybookPage from "./pages/playbook/PlaybookPage";
import CoursesListPage from "./features/courses/pages/CoursesListPage";
import CourseDetailPage from "./features/courses/pages/CourseDetailPage";
import CourseActivePage from "./features/courses/pages/CourseActivePage";
import DirectoriesPage from "./pages/launch/DirectoriesPage";
import ProductHuntGuidePage from "./pages/launch/ProductHuntGuidePage";
import RedditLaunchPage from "./pages/launch/RedditLaunchPage";
import RedditResultsPage from "./pages/launch/RedditResultsPage";
import RedditPostEditor from "./pages/launch/RedditPostEditor";
import RedditDashboardPage from "./pages/reddit/RedditDashboardPage";
import RedditSettings from "./components/reddit/RedditSettings";
import RedditResearchPage from "./pages/reddit/RedditResearchPage";
import RedditSchedulerPage from "./pages/reddit/RedditSchedulerPage";
import RedditContentManager from "./pages/reddit/RedditContentManager";
import RedditAgentSetup from "./components/reddit/RedditAgentSetup";
import ColdOutreachPage from "./pages/launch/ColdOutreachPage";
import PaidAdsPage from "./pages/launch/PaidAdsPage";
import ProductPage from "./pages/research/ProductPage";
import AudiencePage from "./pages/research/AudiencePage";
import KeywordsPage from "./pages/research/KeywordsPage";
import MarketingVaultPage from "./pages/research/MarketingVaultPage";
import Subscription from "./pages/Subscription";
import Onboarding from "./pages/Onboarding";
import { useEffect, useState } from "react";
import CompetitorContent from "./pages/CompetitorContent";
import { supabase } from "@/integrations/supabase/client";
import { HotjarIntegration } from "./integrations/HotjarIntegration";
import SentryErrorBoundary from "@/components/SentryErrorBoundary";
import { SubscriptionGate } from "@/components/auth/SubscriptionGate";
// This component redirects authenticated users to appropriate default pages
const PlaybooksRedirect = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  
  useEffect(() => {
    // Don't redirect if already on onboarding or auth pages
    if (location.pathname.startsWith('/onboarding') || 
        location.pathname.startsWith('/auth/')) {
      return;
    }
    
    // Only redirect if user is authenticated and not already on a specific route
    if (!loading && user && !redirecting) {
      setRedirecting(true);
      
      // Get user's first project to redirect to playbooks with project context
      const getFirstProject = async () => {
        try {
          const { data: projects } = await supabase
            .from('projects')
            .select('id')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true })
            .limit(1);
          
          if (projects && projects.length > 0) {
            // Redirect to first project's playbooks
            navigate(`/projects/${projects[0].id}/playbooks`);
          } else {
            // No projects - redirect to create one
            navigate('/projects');
          }
        } catch (error) {
          console.error('Error fetching projects:', error);
          // Fallback to projects page
          navigate('/projects');
        }
      };
      
      getFirstProject();
    } else if (!loading && !user) {
      // User not authenticated, send to login
      navigate('/auth/login');
    }
  }, [user, loading, navigate, location, redirecting]);
  
  if (loading || redirecting) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>;
  }
  
  return null;
};

// Helper function to detect authentication errors reliably
const isAuthenticationError = (error: any): boolean => {
  if (!error) return false;
  
  // Check error codes first (most reliable)
  if (error.code === 'PGRST301' || error.code === '401' || error.code === '403') {
    return true;
  }
  
  // Check status codes
  if (error.status === 401 || error.status === 403) {
    return true;
  }
  
  // Check specific error messages with regex patterns
  if (error.message) {
    const authErrorPatterns = [
      /JWT/i,
      /session.*expired/i,
      /authentication.*failed/i,
      /invalid.*token/i,
      /unauthorized/i,
      /403/,
      /401/
    ];
    
    return authErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
};

// Enhanced QueryClient configuration with better session handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry authentication errors
      retry: (failureCount, error) => {
        if (isAuthenticationError(error)) {
          return false;
        }
        return failureCount < 3;
      },
      // Exponential backoff for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Consider data stale after 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      // Retry mutations on network errors, but not auth errors
      retry: (failureCount, error) => {
        if (isAuthenticationError(error)) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

// Initialize resources when the app loads
// We don't need to initialize anything specific for Supabase resources anymore
// as we're using SQL migrations to set up storage buckets

const App = () => (
  <SentryErrorBoundary>
    <ThemeProvider defaultTheme="system" storageKey="launchpad-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <BrowserRouter>
            <AuthProvider>
              {/* Add Hotjar integration component for all pages */}
              <HotjarIntegration />
              <Routes>
                {/* Public Pages */}
                <Route path="/" element={<PublicHomepage />} />
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/playbook/:id" element={<PublicCourseDetail />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/features" element={<Features />} />
                <Route path="/ai-agents" element={<Navigate to="/" replace />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/about" element={<About />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/insights" element={<BlogIndexPage />} />
                <Route path="/insights/:slug" element={<BlogPostPage />} />
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/register" element={<Register />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/auth/reddit/callback" element={<RedditCallback />} />
                <Route path="/contentful-test" element={<Navigate to="/" replace />} />
                
                {/* Onboarding - Available to authenticated users, will handle plan checking internally */}
                <Route path="/onboarding" element={<ProjectsPage initialOnboarding={true} />} />
                
                {/* Main Playbooks App - Redirect to first project's playbooks */}
                <Route path="/playbooks" element={<PlaybooksRedirect />} />
                
                {/* Legacy Dashboard and Auth-Required Routes */}
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <SubscriptionGate>
                      <ProjectsPage />
                    </SubscriptionGate>
                  </ProtectedRoute>
                } />
                
                {/* Project Routes - Playbooks with full-screen layout, AI tools with upgrade prompts */}
                <Route path="/projects/:id" element={
                  <ProtectedRoute>
                    <SubscriptionGate>
                      <DashboardLayout />
                    </SubscriptionGate>
                  </ProtectedRoute>
                }>
                  {/* Default to playbooks for "playbooks first" experience */}
                  <Route index element={<Navigate to="playbooks" replace />} />
                  <Route path="dashboard" element={<Navigate to="playbooks" replace />} />
                  <Route path="playbook" element={<Navigate to="playbooks" replace />} />
                  
                  {/* Playbooks with full-screen layout - FIXED: Keep project context */}
                  <Route path="playbooks" element={<CoursesListPage />} />
                  <Route path="playbooks/:playbookId" element={<CourseDetailPage />} />
                  <Route path="playbooks/:playbookId/active" element={<CourseActivePage />} />
                  
                  {/* AI Tools - Show upgrade prompts for non-subscribers */}
                  <Route path="x-agents" element={<XAgentsPage />} />
                  <Route path="analyze" element={<AnalyzePage />} />
                  <Route path="research" element={<ResearchPage />} />
                  <Route path="competitor-content" element={<CompetitorContent />} />
                  <Route path="social-topics" element={<SocialTopicsPage />} />
                  <Route path="topics" element={<ContentTopicsPage />} />
                  <Route path="content" element={<GeneratedContentPage />} />
                  <Route path="content/:contentId/edit" element={<ContentEditorPage />} />
                  <Route path="directories" element={<DirectoriesPage />} />
                  <Route path="producthunt" element={<ProductHuntGuidePage />} />
                  <Route path="launch/reddit" element={<RedditLaunchPage />} />
                  <Route path="reddit/content" element={<RedditContentManager />} />
                  <Route path="reddit/:postId" element={<RedditPostEditor />} />
                  <Route path="reddit/scheduled/:scheduledPostId" element={<RedditPostEditor />} />
                  <Route path="reddit/dashboard" element={<RedditDashboardPage />} />
                  <Route path="reddit/agent-setup" element={<RedditAgentSetup />} />
                  <Route path="reddit/settings" element={<RedditSettings />} />
                  <Route path="reddit/research" element={<RedditResearchPage />} />
                  {/* Redirect old routes to new unified page */}
                  <Route path="reddit" element={<Navigate to="reddit/content" replace />} />
                  <Route path="reddit/scheduler" element={<Navigate to="reddit/content" replace />} />
                  <Route path="cold-outreach" element={<ColdOutreachPage />} />
                  <Route path="paid-ads" element={<PaidAdsPage />} />
                  <Route path="research/product" element={<ProductPage />} />
                  <Route path="research/audience" element={<AudiencePage />} />
                  <Route path="research/keywords" element={<KeywordsPage />} />
                  <Route path="research/marketing-vault" element={<MarketingVaultPage />} />
                  <Route path="subscription" element={<Subscription />} />
                </Route>
                
                {/* Settings Route - Global user settings, not tied to a specific project */}
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <SubscriptionGate>
                      <DashboardLayout />
                    </SubscriptionGate>
                  </ProtectedRoute>
                }>
                  <Route index element={<Settings />} />
                </Route>

                {/* Fallback route to redirect to playbooks */}
                <Route path="*" element={<PlaybooksRedirect />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </SentryErrorBoundary>
);

export default App;
